import { useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useOsmVroom } from '@/api/hooks/useOsmVroom';
import type { VroomRequest, VroomResponse } from '@/types/osm';
import { VEHICLE_COLOURS } from './FullMap';

interface VroomPanelProps {
  onResult: (resp: VroomResponse | null) => void;
}

const DEFAULT_BODY: VroomRequest = {
  vehicles: [
    { id: 1, profile: 'auto', start: [28.2293, -25.7479], end: [28.2293, -25.7479], capacity: [10], description: 'Truck A' },
    { id: 2, profile: 'auto', start: [28.2293, -25.7479], end: [28.2293, -25.7479], capacity: [10], description: 'Truck B' },
  ],
  jobs: [
    { id: 1, location: [28.2074, -25.8003], description: 'Centurion Mall', delivery: [3] },
    { id: 2, location: [28.0567, -26.1076], description: 'Sandton City', delivery: [2] },
    { id: 3, location: [28.0473, -26.2041], description: 'JHB CBD', delivery: [1] },
    { id: 4, location: [28.1894, -25.8603], description: 'Centurion North', delivery: [4] },
    { id: 5, location: [27.854, -26.2485], description: 'Soweto', delivery: [2] },
    { id: 6, location: [28.0731, -25.9986], description: 'Midrand', delivery: [3] },
  ],
};

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 1 });

function toCsv(resp: VroomResponse): string {
  const rows: string[] = ['vehicle,step,type,job,arrival_min,duration_min,lon,lat'];
  for (const r of resp.routes) {
    let i = 0;
    for (const s of r.steps) {
      rows.push(
        [
          r.vehicle,
          ++i,
          s.type,
          s.id ?? s.job ?? '',
          (s.arrival / 60).toFixed(1),
          (s.duration / 60).toFixed(1),
          s.location?.[0] ?? '',
          s.location?.[1] ?? '',
        ].join(','),
      );
    }
  }
  return rows.join('\n');
}

export default function VroomPanel({ onResult }: VroomPanelProps) {
  const [text, setText] = useState(() => JSON.stringify(DEFAULT_BODY, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const mut = useOsmVroom();

  async function solve() {
    setParseError(null);
    let body: VroomRequest;
    try {
      body = JSON.parse(text);
    } catch (e) {
      setParseError((e as Error).message);
      return;
    }
    try {
      const resp = await mut.mutateAsync(body);
      onResult(resp);
    } catch {
      onResult(null);
    }
  }

  function copyCsv() {
    if (!mut.data) return;
    const csv = toCsv(mut.data);
    void navigator.clipboard.writeText(csv);
  }

  const resp = mut.data;
  const error = (mut.error as Error | null) ?? null;

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto p-3">
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">VROOM fleet planning</h3>
        <p className="text-xs text-muted-foreground">
          Edit the JSON request to define vehicles + jobs. Each vehicle gets a polyline.
        </p>
      </div>

      <textarea
        className="min-h-[180px] flex-shrink-0 rounded-md border bg-muted/40 p-2 font-mono text-[11px]"
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
      />

      <div className="flex gap-2">
        <Button type="button" size="sm" className="flex-1" onClick={() => void solve()} disabled={mut.isPending}>
          {mut.isPending ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Play className="mr-1 h-3.5 w-3.5" />}
          Solve
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setText(JSON.stringify(DEFAULT_BODY, null, 2));
            mut.reset();
            onResult(null);
          }}
        >
          Reset
        </Button>
      </div>

      {parseError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-2 text-xs text-red-800">JSON: {parseError}</CardContent>
        </Card>
      )}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-2 text-xs text-red-800">{error.message}</CardContent>
        </Card>
      )}

      {resp && (
        <Card>
          <CardContent className="space-y-2 p-3 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total distance</span>
              <span className="font-semibold">{NUM.format((resp.summary.distance ?? 0) / 1000)} km</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total duration</span>
              <span className="font-semibold">{NUM.format(resp.summary.duration / 60)} min</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Vehicles used</span>
              <span>{resp.routes.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Unassigned</span>
              <span className={(resp.unassigned ?? []).length ? 'text-red-700' : ''}>
                {(resp.unassigned ?? []).length}
              </span>
            </div>
            <div className="space-y-1 border-t pt-2">
              {resp.routes.map((r, i) => {
                const c = VEHICLE_COLOURS[i % VEHICLE_COLOURS.length];
                const jobs = r.steps.filter((s) => s.type === 'job').length;
                return (
                  <details key={r.vehicle} className="rounded-md border-l-[3px] pl-2" style={{ borderColor: c }}>
                    <summary className="cursor-pointer">
                      <span className="font-semibold">Vehicle {r.vehicle}</span>{' '}
                      <span className="text-muted-foreground">
                        · {((r.distance ?? 0) / 1000).toFixed(1)} km · {(r.duration / 60).toFixed(0)} min · {jobs} jobs
                      </span>
                    </summary>
                    <ol className="mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                      {r.steps.map((s, k) => (
                        <li key={k} className="flex items-baseline justify-between">
                          <span>
                            <span className="text-foreground">{s.type}</span>
                            {s.id != null && <span> #{s.id}</span>}
                          </span>
                          <span>{(s.arrival / 60).toFixed(0)} m</span>
                        </li>
                      ))}
                    </ol>
                  </details>
                );
              })}
            </div>
            <button
              type="button"
              className="text-xs text-primary underline-offset-2 hover:underline"
              onClick={copyCsv}
            >
              Copy as CSV
            </button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
